import { DefMM05ForceCase } from 'src/app/masterdata/models/config-version/DefMM05ForceCase';
import { DefMM07Posture } from 'src/app/masterdata/models/config-version/DefMM07Posture';
import { MM07PartType } from 'src/app/masterdata/models/config-version/MM07PartType';
import { MM07ForceCaseType } from '../../analysis/models/criterions/MM07ForceCaseType';
import { MM07MovementVelocity } from '../../analysis/models/criterions/MM07MovementVelocity';
import { MM07PositioningPrecision } from '../../analysis/models/criterions/MM07PositioningPrecision';
import { GloveForceCase, GloveForceCaseMM04Type, GloveForceCaseMM05Type, GloveForceCaseMM07Type } from '../../dwsm/models/GloveForceCaseType';
import { ImageItem } from 'src/app/shared/ui/sera-image/models/image-item';
import { TranslationUtils } from './translation-utils';
import { DefMM04Posture } from 'src/app/masterdata/models/config-version/DefMM04Posture';
import { DataMM07Posture } from '../../analysis/models/criterions/DataMM07Posture';
import { DataMM07PostureItem } from '../../analysis/cr7/cr7-kim/force-case-popup/models/DataMM07PostureItem';
import { DefMM05WristPosture } from '../../masterdata/models/config-version/DefMM05WristPosture';
import { ForceCaseImageItem } from '../../analysis/models/criterions/ForceCaseImageItem';
import { SelectListOption } from '@bmw-ds/components';

export const MM04_ASSETS_PATH = 'assets/images/MM04/';
export const MM05_ASSETS_PATH = 'assets/images/MM05/';
export const MM07_ASSETS_PATH = 'assets/images/MM07/postures/';

export class ForceCaseUtils {
    static readonly MM07Tooltips: { [key: string]: string } = {
        [MM07ForceCaseType.LIFT]: 'FORCE_CASE.MM_07.LIFT.TOOLTIP.LBL',
        [MM07ForceCaseType.CARRY]: 'FORCE_CASE.MM_07.CARRY.TOOLTIP.LBL',
        [MM07ForceCaseType.HOLD]: 'FORCE_CASE.MM_07.HOLD.TOOLTIP.LBL'
    };

    public static convertToMM04ForceCaseType(forceCaseType: GloveForceCase) {
        return forceCaseType as GloveForceCaseMM04Type;
    }

    public static convertToMM07ForceCaseType(forceCaseType: GloveForceCase): GloveForceCaseMM07Type {
        return forceCaseType as GloveForceCaseMM07Type;
    }

    public static convertToMM05ForceCaseType(forceCaseType: GloveForceCase): GloveForceCaseMM05Type {
        return forceCaseType as GloveForceCaseMM05Type;
    }

    public static getMM07ToolTipBasedOnType(selectedTab: string) {
        return this.MM07Tooltips[selectedTab];
    }

    public static isMM07ForceCasePullAndPush(forceCaseType: GloveForceCase) {
        const forceCaseTypeMM07 = this.convertToMM07ForceCaseType(forceCaseType);
        return forceCaseTypeMM07.type === MM07ForceCaseType.PULL_AND_PUSH;
    }

    public static mapMM07ForceTypeToString(forceCaseType: GloveForceCase): string {
        const typeMapping: { [key: number]: string } = {
            0: 'PAGE.MM_07.LIFT.LBL',
            1: 'XSENS_FORCE_CASES_COLUMN_4_HOLD.LBL',
            2: 'XSENS_FORCE_CASES_COLUMN_4_CARRY.LBL',
            3: 'PAGE.MM_07.DRAW.LBL'
        };
        return typeMapping[this.convertToMM07ForceCaseType(forceCaseType).type].toString();
    }

    static readonly positioningPrecisionOptions: SelectListOption[] = Object.entries(MM07PositioningPrecision).map(e => ({
        id: e[0],
        label: 'PAGE.MM_07.POSITIONING_ACCURACY_ENUM.' + e[0] + '.LBL'
    }));

    static readonly movementVelocityOptions: SelectListOption[] = Object.entries(MM07MovementVelocity).map(e => ({
        id: e[0],
        label: 'PAGE.MM_07.MOVING_SPEED_ENUM.' + e[0] + '.LBL'
    }));

    public static buildDefMM04Images(postures: DefMM04Posture[], userLanguage: string, isTorque: boolean): ForceCaseImageItem[] {
        return postures.map(posture => {
            return {
                id: posture.id,
                src: MM04_ASSETS_PATH + posture.forceCaseNamePicture,
                alt: TranslationUtils.translateTextValue(posture.description, userLanguage),
                label: TranslationUtils.translateTextValue(posture.description, userLanguage),
                textAlign: 'left',
                isSelected: false,
                isSelectable: isTorque ? posture.torque : true,
                isTorque: posture.torque
            } as ForceCaseImageItem;
        });
    }

    public static buildDefMM05Images(forceCases: DefMM05ForceCase[], userLanguage: string, setLabels: boolean): ForceCaseImageItem[] {
        return forceCases.map(forceCase => {
            return {
                id: forceCase.id,
                src: MM05_ASSETS_PATH + forceCase.forceCaseNamePicture,
                alt: TranslationUtils.translateTextValue(forceCase.description, userLanguage),
                label: setLabels ? TranslationUtils.translateTextValue(forceCase.description, userLanguage) : undefined,
                textAlign: 'left',
                isSelected: false,
                isSelectable: true,
                isTorque: forceCase.torque
            } as ForceCaseImageItem;
        });
    }

    public static buildDefMM05WristPositionImages(wristPositions: DefMM05WristPosture[], userLanguage: string, setLabels: boolean): ImageItem[] {
        return wristPositions.map(wristPosition => {
            return {
                id: wristPosition.id,
                src: MM05_ASSETS_PATH + wristPosition.wirstPositionNamePicture,
                alt: TranslationUtils.translateTextValue(wristPosition.description, userLanguage),
                label: setLabels ? TranslationUtils.translateTextValue(wristPosition.description, userLanguage) : undefined,
                textAlign: 'left',
                isSelected: false,
                isSelectable: true
            } as ImageItem;
        });
    }

    public static buildDefMM05ImagesForDWSM(forceCases: DefMM05ForceCase[], userLanguage: string, selectedGloveFeature: number): ImageItem[] {
        let counter = 1;
        return forceCases.map(forceCase => {
            return {
                id: counter === 6 ? ++counter : counter,
                src: MM05_ASSETS_PATH + forceCase.forceCaseNamePicture,
                alt: TranslationUtils.translateTextValue(forceCase.description, userLanguage),
                label: TranslationUtils.translateTextValue(forceCase.description, userLanguage),
                textAlign: 'center',
                isSelected: selectedGloveFeature === counter++,
                isSelectable: true
            } as ImageItem;
        });
    }

    public static buildDefMM07PostureImages(postures: DefMM07Posture[], userLanguage: string, type: MM07PartType): ImageItem[] {
        let counter = 0;
        return postures.map(posture => {
            return {
                id: counter,
                src:
                    type === MM07PartType.PULL_PUSH
                        ? MM07_ASSETS_PATH + `MM7_7_PULL_PUSH.${counter++}.gif`
                        : MM07_ASSETS_PATH + `MM7_7_TRANSFER.${counter++}.gif`,
                alt: TranslationUtils.translateTextValue(posture.description, userLanguage),
                label: TranslationUtils.translateTextValue(posture.description, userLanguage),
                textAlign: 'center',
                isSelected: false,
                isSelectable: true
            } as ImageItem;
        });
    }

    public static buildImagePlaceHolderFromDefMM04AndBodyPose(defMM04Posture: DefMM04Posture, bodyPose: number): ImageItem {
        return {
            id: bodyPose,
            src: MM04_ASSETS_PATH + defMM04Posture.forceCaseNamePicture,
            alt: '',
            label: '',
            textAlign: 'center',
            isSelected: false,
            isSelectable: true
        } as ImageItem;
    }

    public static buildImagePlaceHolderFromDefMM05AndGloveFeature(defMM05ForceCase: DefMM05ForceCase, gloveFeature: number): ImageItem {
        return {
            id: gloveFeature,
            src: MM05_ASSETS_PATH + defMM05ForceCase.forceCaseNamePicture,
            alt: '',
            label: '',
            textAlign: 'center',
            isSelected: false,
            isSelectable: true
        } as ImageItem;
    }

    public static buildImagePlaceHolderFromGloveForceCaseTypeMM07(gloveForceCaseType: GloveForceCaseMM07Type): ImageItem {
        return {
            id: gloveForceCaseType.bodyPosture,
            src: this.isMM07ForceCasePullAndPush(gloveForceCaseType)
                ? MM07_ASSETS_PATH + `MM7_7_PULL_PUSH.${gloveForceCaseType.bodyPosture}.gif`
                : MM07_ASSETS_PATH + `MM7_7_TRANSFER.${gloveForceCaseType.bodyPosture}.gif`,
            alt: '',
            label: '',
            textAlign: 'center',
            isSelected: false,
            isSelectable: true
        };
    }

    public static buildDefMM04ImagesForDWSM(postures: DefMM04Posture[], userLanguage: string, selectedPosture: number): ImageItem[] {
        let counter = 1;
        return postures.map(posture => {
            return {
                id: counter,
                src: MM04_ASSETS_PATH + posture.forceCaseNamePicture,
                alt: TranslationUtils.translateTextValue(posture.description, userLanguage),
                label: TranslationUtils.translateTextValue(posture.description, userLanguage),
                textAlign: 'center',
                isSelected: selectedPosture === counter++,
                isSelectable: true
            } as ImageItem;
        });
    }

    public static mapPostures(
        defPostures: DefMM07Posture[] | undefined,
        forceCasePostures: DataMM07Posture[] | undefined,
        selectedImage: number,
        partId: number,
        isPullAndPush: boolean,
        userLanguage: string
    ): DataMM07PostureItem[] {
        return defPostures
            ? defPostures.map((posture, index) => {
                  const matchingForceCasePosture = forceCasePostures?.find(fcPosture => fcPosture.defPostureId === posture.id);
                  return {
                      id: matchingForceCasePosture ? matchingForceCasePosture.id : undefined,
                      partId: partId,
                      defPostureId: posture.id,
                      percentage: matchingForceCasePosture ? matchingForceCasePosture.percentage : 0,
                      image: {
                          id: index,
                          src: isPullAndPush
                              ? `assets/images/MM07/postures/MM7_7_PULL_PUSH.${index}.gif`
                              : `assets/images/MM07/postures/MM7_7_TRANSFER.${index}.gif`,
                          alt: TranslationUtils.translateTextValue(posture.description, userLanguage)!,
                          label: TranslationUtils.translateTextValue(posture.description, userLanguage),
                          textAlign: 'center',
                          isSelected: selectedImage === posture.id,
                          isSelectable: true
                      }
                  };
              })
            : [];
    }
}
